import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { STATUSES, PRIORITIES, STATUS_COLORS } from "@/lib/constants";
import { StatusBadge, PriorityBadge } from "@/components/Badges";
import TaskFormModal from "@/components/TaskFormModal";
import TaskDetailDrawer from "@/components/TaskDetailDrawer";
import { Plus, Upload, Download, Filter, Search, Loader2, ExternalLink } from "lucide-react";

const TABS = [
  { key: "my", label: "My Tasks" },
  { key: "kanban", label: "Kanban Board" },
  { key: "table", label: "Table" },
  { key: "backlog", label: "Backlog" },
];

export default function WorkTracker() {
  const { user, isAdmin } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [tab, setTab] = useState("my");
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [drawerId, setDrawerId] = useState(null);
  const [filters, setFilters] = useState({ priority: "", client: "", q: "" });
  const [importOpen, setImportOpen] = useState(false);

  const load = () => {
    setLoading(true);
    const scope = tab === "my" ? "mine" : "all";
    api.get(`/tasks?scope=${scope}`)
      .then(r => setTasks(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  // Deep link: ?task=<id>
  useEffect(() => {
    const params = new URLSearchParams(loc.search);
    const id = params.get("task");
    if (id) setDrawerId(id);
  }, [loc.search]);

  const visibleTasks = useMemo(() => {
    let list = tasks;
    if (tab === "backlog") list = list.filter(t => t.priority === "P2" && t.status === "Not Started");
    if (filters.priority) list = list.filter(t => t.priority === filters.priority);
    if (filters.client) list = list.filter(t => t.client_name === filters.client);
    if (filters.q) {
      const q = filters.q.toLowerCase();
      list = list.filter(t => t.title.toLowerCase().includes(q) || t.project?.toLowerCase().includes(q));
    }
    return list;
  }, [tasks, tab, filters]);

  const clientOptions = useMemo(() => [...new Set(tasks.map(t => t.client_name).filter(Boolean))], [tasks]);

  const onTaskClick = (t) => { setDrawerId(t.id); nav(`/work-tracker?task=${t.id}`); };
  const closeDrawer = () => { setDrawerId(null); nav("/work-tracker"); };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-gray-500">Work Tracker</div>
          <h1 className="text-3xl font-semibold tracking-tight text-gray-900 mt-2">Tasks & assignments</h1>
          <p className="text-sm text-gray-500 mt-1.5">
            {isAdmin ? "All team tasks across clients and projects." : "Your assigned tasks."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <button onClick={() => setImportOpen(true)} data-testid="csv-import-btn" className="text-sm border border-gray-300 hover:border-navy hover:text-navy px-3.5 py-2 rounded-md font-medium inline-flex items-center gap-2">
              <Upload className="w-4 h-4" /> Import CSV
            </button>
          )}
          <button onClick={() => { setEditing(null); setShowModal(true); }} data-testid="create-task-btn" className="bg-navy hover:bg-navy-800 text-white px-4 py-2 rounded-md font-medium text-sm inline-flex items-center gap-2">
            <Plus className="w-4 h-4" /> New task
          </button>
        </div>
      </header>

      {/* Tabs */}
      <div className="border-b border-gray-200 flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-1">
          {TABS.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`tab-${t.key}`}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                tab === t.key ? "border-navy text-navy" : "border-transparent text-gray-500 hover:text-navy"
              }`}
            >{t.label}</button>
          ))}
        </div>

        <div className="flex items-center gap-2 pb-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              placeholder="Search…"
              value={filters.q}
              onChange={(e) => setFilters(f => ({ ...f, q: e.target.value }))}
              data-testid="task-search"
              className="pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-navy/20 focus:border-navy"
            />
          </div>
          <select value={filters.priority} onChange={(e) => setFilters(f => ({ ...f, priority: e.target.value }))} data-testid="filter-priority" className="text-sm border border-gray-300 rounded-md px-2 py-1.5">
            <option value="">All priority</option>
            {PRIORITIES.map(p => <option key={p}>{p}</option>)}
          </select>
          <select value={filters.client} onChange={(e) => setFilters(f => ({ ...f, client: e.target.value }))} data-testid="filter-client" className="text-sm border border-gray-300 rounded-md px-2 py-1.5">
            <option value="">All clients</option>
            {clientOptions.map(c => <option key={c}>{c}</option>)}
          </select>
        </div>
      </div>

      {/* Views */}
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-400 py-12"><Loader2 className="w-4 h-4 animate-spin" /> Loading tasks…</div>
      ) : (
        <>
          {(tab === "my" || tab === "backlog") && <ListView tasks={visibleTasks} onTask={onTaskClick} groupByStatus={tab === "my"} />}
          {tab === "kanban" && <KanbanView tasks={visibleTasks} onTask={onTaskClick} reload={load} canDrag={true} user={user} isAdmin={isAdmin} />}
          {tab === "table" && <TableView tasks={visibleTasks} onTask={onTaskClick} />}
        </>
      )}

      <TaskFormModal open={showModal} task={editing} onClose={() => setShowModal(false)} onSaved={load} />
      <TaskDetailDrawer
        taskId={drawerId}
        onClose={closeDrawer}
        onChanged={load}
        onEdit={() => {
          const t = tasks.find(x => x.id === drawerId);
          if (t) { setEditing(t); setShowModal(true); }
        }}
      />
      {importOpen && <CsvImportModal onClose={() => setImportOpen(false)} onDone={load} />}
    </div>
  );
}

function ListView({ tasks, onTask, groupByStatus }) {
  if (tasks.length === 0) return <Empty msg="No tasks here." />;
  if (!groupByStatus) {
    return <Card><TaskTable tasks={tasks} onTask={onTask} /></Card>;
  }
  // Group by status, ordered per STATUSES
  const groups = STATUSES.map(s => ({ status: s, items: tasks.filter(t => t.status === s) })).filter(g => g.items.length > 0);
  return (
    <div className="space-y-4">
      {groups.map(g => (
        <Card key={g.status}>
          <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2.5">
            <StatusBadge status={g.status} />
            <span className="text-xs text-gray-500">{g.items.length} tasks</span>
          </div>
          <TaskTable tasks={g.items} onTask={onTask} hideStatus />
        </Card>
      ))}
    </div>
  );
}

function TaskTable({ tasks, onTask, hideStatus = false }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-50/60 text-[11px] uppercase tracking-wider text-gray-500">
          <tr>
            <th className="text-left px-5 py-2.5 font-medium">Task</th>
            <th className="text-left px-3 py-2.5 font-medium">Project / IP</th>
            <th className="text-left px-3 py-2.5 font-medium">Format</th>
            <th className="text-left px-3 py-2.5 font-medium">Assignee</th>
            {!hideStatus && <th className="text-left px-3 py-2.5 font-medium">Status</th>}
            <th className="text-left px-3 py-2.5 font-medium">Priority</th>
            <th className="text-left px-3 py-2.5 font-medium">Due</th>
            <th className="text-left px-5 py-2.5 font-medium">Doc</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {tasks.map(t => (
            <tr key={t.id} data-testid={`task-row-${t.id}`} onClick={() => onTask(t)} className="hover:bg-gray-50 cursor-pointer">
              <td className="px-5 py-3 font-medium text-gray-900">{t.title}</td>
              <td className="px-3 py-3 text-gray-700">{t.project}</td>
              <td className="px-3 py-3 text-gray-500">{t.format}</td>
              <td className="px-3 py-3 text-gray-700">{t.assignee_name}</td>
              {!hideStatus && <td className="px-3 py-3"><StatusBadge status={t.status} /></td>}
              <td className="px-3 py-3"><PriorityBadge priority={t.priority} /></td>
              <td className="px-3 py-3 text-gray-700 font-mono text-xs">{t.due_date || "—"}</td>
              <td className="px-5 py-3">
                {t.google_doc_link ? (
                  <a href={t.google_doc_link} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-navy hover:underline inline-flex items-center gap-1 text-xs">
                    Open <ExternalLink className="w-3 h-3" />
                  </a>
                ) : <span className="text-gray-300 text-xs">—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TableView({ tasks, onTask }) {
  if (tasks.length === 0) return <Empty msg="No tasks match your filters." />;
  return <Card><TaskTable tasks={tasks} onTask={onTask} /></Card>;
}

function KanbanView({ tasks, onTask, reload, isAdmin, user }) {
  const columns = STATUSES.filter(s => s !== "Closed"); // closed hidden by default per PRD section 4.4
  const handleDrop = async (taskId, status) => {
    const t = tasks.find(x => x.id === taskId);
    if (!t || t.status === status) return;
    // Permission check (mirrors backend)
    if (!isAdmin && t.assigned_to !== user.id) return;
    await api.patch(`/tasks/${taskId}`, { status });
    reload();
  };
  return (
    <div data-testid="kanban-board" className="flex gap-4 overflow-x-auto kanban-scroll pb-4 -mx-2 px-2">
      {columns.map(col => {
        const items = tasks.filter(t => t.status === col);
        const color = STATUS_COLORS[col];
        return (
          <div
            key={col}
            data-testid={`kanban-col-${col.toLowerCase().replace(/\s+/g, "-")}`}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { const id = e.dataTransfer.getData("text/plain"); handleDrop(id, col); }}
            className="w-72 flex-shrink-0 bg-gray-50/70 rounded-lg border border-gray-200 flex flex-col"
          >
            <div className="px-3 py-2.5 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color.text }} />
                <span className="text-xs font-semibold uppercase tracking-wider text-gray-700">{col}</span>
              </div>
              <span className="text-[10px] text-gray-400 font-mono">{items.length}</span>
            </div>
            <div className="p-2 space-y-2 min-h-[200px]">
              {items.map(t => (
                <div
                  key={t.id}
                  draggable
                  onDragStart={(e) => e.dataTransfer.setData("text/plain", t.id)}
                  onClick={() => onTask(t)}
                  data-testid={`kanban-card-${t.id}`}
                  className="bg-white border border-gray-200 rounded-md p-3 shadow-sm hover:shadow hover:border-navy/40 cursor-pointer transition-all"
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="text-sm font-medium text-gray-900 leading-snug line-clamp-2">{t.title}</div>
                    <PriorityBadge priority={t.priority} />
                  </div>
                  <div className="text-[11px] text-gray-500 uppercase tracking-wider mb-2">{t.project}</div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="inline-flex items-center gap-1.5 text-gray-700">
                      <span className="w-5 h-5 rounded-full bg-navy/10 text-navy font-semibold flex items-center justify-center text-[10px]">
                        {(t.assignee_name || "?").slice(0, 1)}
                      </span>
                      {t.assignee_name}
                    </span>
                    <span className="text-gray-400 font-mono text-[10px]">{t.due_date}</span>
                  </div>
                </div>
              ))}
              {items.length === 0 && <div className="text-[11px] text-gray-300 italic px-2 py-4 text-center">—</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Card({ children }) {
  return <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">{children}</div>;
}
function Empty({ msg }) {
  return <div className="bg-white border border-dashed border-gray-200 rounded-lg p-10 text-center text-sm text-gray-400">{msg}</div>;
}

function CsvImportModal({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/tasks/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data); onDone && onDone();
    } catch (e) {
      setResult({ created: 0, errors: [e.response?.data?.detail || "Upload failed"] });
    } finally { setBusy(false); }
  };

  const downloadTemplate = async () => {
    const r = await api.get("/tasks/import/template", { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([r.data]));
    const a = document.createElement("a"); a.href = url; a.download = "neev_tasks_template.csv"; a.click();
  };

  return (
    <div className="fixed inset-0 z-50 bg-gray-900/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white max-w-md w-full rounded-xl shadow-xl border border-gray-200 p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="csv-import-modal">
        <div>
          <h2 className="text-lg font-semibold tracking-tight">Import tasks from CSV</h2>
          <p className="text-xs text-gray-500 mt-1">Up to 500 rows per import. Get the template, fill it, then upload.</p>
        </div>
        <button onClick={downloadTemplate} data-testid="csv-template-btn" className="text-sm text-navy hover:underline inline-flex items-center gap-1.5">
          <Download className="w-3.5 h-3.5" /> Download template
        </button>
        <input type="file" accept=".csv" onChange={(e) => setFile(e.target.files[0])} data-testid="csv-file-input" className="block w-full text-sm" />
        {result && (
          <div className="text-sm bg-gray-50 border border-gray-200 rounded p-3 space-y-1">
            <div className="text-emerald-700 font-medium">{result.created} tasks created</div>
            {result.errors?.length > 0 && (
              <ul className="text-red-600 text-xs space-y-0.5 max-h-32 overflow-y-auto">
                {result.errors.map((e) => <li key={e}>• {e}</li>)}
              </ul>
            )}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded">Close</button>
          <button onClick={upload} disabled={!file || busy} data-testid="csv-upload-btn" className="bg-navy hover:bg-navy-800 text-white px-4 py-2 rounded text-sm font-medium disabled:opacity-50 inline-flex items-center gap-2">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}Upload
          </button>
        </div>
      </div>
    </div>
  );
}
